import { RECIPES_DOC, type Ingredient, type Recipe, type RecipePhotoRef } from '@awwyeah/shared';
import type * as Y from 'yjs';
import { useYDoc, useYMap } from '../../sync';
import { newId } from './utils/id';

/** The `recipes` Yjs doc holds a single `Y.Map<recipeId, Recipe>` under this key. */
export const RECIPES_MAP_KEY = 'recipes';

/** Opens the shared `recipes` doc for as long as the caller is mounted. */
export function useRecipesDoc(): Y.Doc {
  return useYDoc(RECIPES_DOC);
}

/** Live list of every recipe, newest first. */
export function useRecipes(doc: Y.Doc): { map: Y.Map<Recipe>; recipes: Recipe[] } {
  const { map, entries } = useYMap<Recipe>(doc, RECIPES_MAP_KEY);
  const recipes = entries
    .map(([, recipe]) => recipe)
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  return { map, recipes };
}

/** Live single recipe by id (or `undefined` once deleted). */
export function useRecipe(doc: Y.Doc, id: string | undefined): Recipe | undefined {
  const { recipes } = useRecipes(doc);
  return id ? recipes.find((r) => r.id === id) : undefined;
}

/** A draft ingredient row. WS-18 widened this with the fields `clean-recipe` produces. */
export interface DraftIngredient {
  text: string;
  quantity?: string;
  quantityMax?: string;
  unit?: string;
  name?: string;
  prep?: string;
  optional?: boolean;
}

export interface RecipeDraft {
  title: string;
  ingredients: DraftIngredient[];
  steps: string[];
  sourceUrl?: string;
  tags: string[];
  notes?: string;
  servings?: number;
  /** Set when the importer already downloaded/stored a header photo (WS-20) — carried
   * through create/update so the form's pre-fill isn't lost on save. The detail page's own
   * upload/change control writes `photoRef` directly via `setRecipePhotoRef`, not this. */
  photoRef?: RecipePhotoRef;
}

function toIngredients(draft: RecipeDraft['ingredients']): Ingredient[] {
  return draft
    .filter((line) => line.text.trim().length > 0)
    .map((line) => ({
      id: newId(),
      text: line.text.trim(),
      quantity: line.quantity,
      quantityMax: line.quantityMax,
      unit: line.unit,
      name: line.name,
      prep: line.prep,
      optional: line.optional,
    }));
}

/** Creates a recipe and writes it to the doc. Returns the new id. */
export function createRecipe(map: Y.Map<Recipe>, draft: RecipeDraft, userId: string): string {
  const id = newId();
  const now = new Date().toISOString();
  const recipe: Recipe = {
    id,
    title: draft.title.trim() || 'Untitled recipe',
    ingredients: toIngredients(draft.ingredients),
    steps: draft.steps.map((s) => s.trim()).filter(Boolean),
    sourceUrl: draft.sourceUrl,
    tags: draft.tags.map((t) => t.trim()).filter(Boolean),
    notes: draft.notes?.trim() || undefined,
    servings: draft.servings,
    photoRef: draft.photoRef,
    createdBy: userId,
    createdAt: now,
    updatedBy: userId,
    updatedAt: now,
  };
  map.set(id, recipe);
  return id;
}

/** Updates an existing recipe in place, preserving `createdBy`/`createdAt`. */
export function updateRecipe(
  map: Y.Map<Recipe>,
  id: string,
  draft: RecipeDraft,
  userId: string,
): void {
  const existing = map.get(id);
  if (!existing) return;
  const now = new Date().toISOString();
  map.set(id, {
    ...existing,
    title: draft.title.trim() || 'Untitled recipe',
    ingredients: toIngredients(draft.ingredients),
    steps: draft.steps.map((s) => s.trim()).filter(Boolean),
    sourceUrl: draft.sourceUrl,
    tags: draft.tags.map((t) => t.trim()).filter(Boolean),
    notes: draft.notes?.trim() || undefined,
    servings: draft.servings,
    // The form itself never manages photoRef — that's the detail page's upload/change
    // control (`setRecipePhotoRef`). This only carries a *new* value through when the
    // importer pre-filled one and the user saves the editor without touching the photo.
    photoRef: draft.photoRef ?? existing.photoRef,
    updatedBy: userId,
    updatedAt: now,
  });
}

/** Writes a freshly uploaded/replaced header photo directly (WS-20's detail-page upload
 * control) — bypasses the full draft round-trip since only one field is changing. */
export function setRecipePhotoRef(
  map: Y.Map<Recipe>,
  id: string,
  photoRef: RecipePhotoRef,
  userId: string,
): void {
  const existing = map.get(id);
  if (!existing) return;
  map.set(id, {
    ...existing,
    photoRef,
    updatedBy: userId,
    updatedAt: new Date().toISOString(),
  });
}

/** Deletes a recipe and returns it (or `undefined`), so the caller can offer undo. */
export function deleteRecipe(map: Y.Map<Recipe>, id: string): Recipe | undefined {
  const removed = map.get(id);
  if (removed) map.delete(id);
  return removed;
}

/** Restores a previously deleted recipe verbatim — the undo half of `deleteRecipe`. */
export function restoreRecipe(map: Y.Map<Recipe>, recipe: Recipe): void {
  map.set(recipe.id, recipe);
}
